import { ReactNode } from "react";

import { Button, ButtonProps, Group, MantineColor, Modal, Stack } from "@mantine/core";

import { TextWrapper } from "../TextWrapper";

type ConfirmationModalProps = {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  title: string;
  description: ReactNode;
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmButtonColor?: MantineColor;
  /** Styling only. Cannot override the click handler or the in-flight disabled state. */
  cancelButtonProps?: Omit<ButtonProps, "disabled" | "loading">;
  /** Allows escape, click-outside, and a close button to dismiss the modal while idle. */
  allowDismiss?: boolean;
  zIndex?: number;
  modalTestId?: string;
  cancelButtonTestId?: string;
  confirmButtonTestId?: string;
};

function ConfirmationModal({
  description,
  isDeleting,
  zIndex,
  onClose,
  onConfirm,
  opened,
  title,
  cancelButtonText = "Cancel",
  confirmButtonText = "Yes, Delete",
  confirmButtonColor = "red.6",
  cancelButtonProps,
  allowDismiss = false,
  modalTestId,
  cancelButtonTestId,
  confirmButtonTestId,
}: ConfirmationModalProps) {
  // The write is already in flight, so dismissing would leave the user without its outcome.
  const handleClose = () => {
    if (isDeleting) return;
    onClose();
  };

  const canDismiss = allowDismiss && !isDeleting;

  return (
    <Modal.Root
      centered
      opened={opened}
      zIndex={zIndex}
      onClose={handleClose}
      closeOnEscape={canDismiss}
      closeOnClickOutside={canDismiss}
    >
      <Modal.Overlay />
      {/* Test IDs target the content element, so the modal's text is inside the queried node. */}
      <Modal.Content data-testid={modalTestId}>
        <Modal.Header>
          <Group align="center" justify="space-between">
            <Modal.Title fz="h3" fw={600}>
              {title}
            </Modal.Title>
          </Group>
          {canDismiss && <Modal.CloseButton aria-label="Close" />}
        </Modal.Header>
        <Modal.Body p="md">
          <Stack gap="md">
            <TextWrapper size="lg">{description}</TextWrapper>
            <Group justify="flex-end">
              <Button
                fw={600}
                variant="default"
                {...cancelButtonProps}
                onClick={handleClose}
                disabled={isDeleting}
                data-testid={cancelButtonTestId}
              >
                {cancelButtonText}
              </Button>
              <Button
                fw={600}
                variant="filled"
                onClick={onConfirm}
                loading={isDeleting}
                color={confirmButtonColor}
                data-testid={confirmButtonTestId}
              >
                {confirmButtonText}
              </Button>
            </Group>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

export default ConfirmationModal;
